import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Rating,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  LocationOn,
  BusinessCenter,
  School,
  AttachMoney,
  Source,
  Description,
  Edit,
  Delete,
  CalendarToday,
  Notes,
  Star,
} from '@mui/icons-material';
import { getCandidate } from '../../services/candidateService';
import { Candidate, statusColors, experienceLabels } from '../../types/Candidate';
import { useThemeContext } from '../../context/ThemeContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`candidate-tabpanel-${index}`}
      aria-labelledby={`candidate-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CandidateDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';
  
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [tabValue, setTabValue] = useState(0);
  
  useEffect(() => {
    if (id) {
      // In a real application, you would fetch the candidate from an API
      const candidateData = getCandidate(parseInt(id, 10));
      if (candidateData) {
        setCandidate(candidateData);
      }
      setLoading(false);
    }
  }, [id]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleEdit = () => {
    navigate(`/candidates/${id}/edit`);
  };
  
  const handleDelete = () => {
    // In a real app, you would call an API to delete the candidate
    navigate('/candidates');
  };
  
  const handleBack = () => {
    navigate('/candidates');
  };
  
  if (loading) {
    return <Typography>Loading...</Typography>;
  }
  
  if (!candidate) {
    return <Typography>Candidate not found</Typography>;
  }

  return (
    <Box>
      {/* Header with back button and actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Button
          variant="text"
          color="inherit"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ textTransform: 'none' }}
        >
          Back to Candidates
        </Button>
        
        <Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Edit />}
            onClick={handleEdit}
            sx={{ mr: 2, borderRadius: 2, textTransform: 'none' }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDelete}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      {/* Candidate header card */}
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          boxShadow: 'none',
          overflow: 'hidden',
          border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
        }}
      >
        <Box
          sx={{
            height: 8,
            width: '100%',
            backgroundColor: statusColors[candidate.status],
          }}
        />
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: isDark
                      ? alpha(statusColors[candidate.status], 0.2)
                      : alpha(statusColors[candidate.status], 0.1),
                    color: statusColors[candidate.status],
                    fontWeight: 'bold',
                    fontSize: '1.5rem',
                    mr: 3,
                  }}
                >
                  {candidate.name
                    .split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()}
                </Avatar>
                
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {candidate.name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip
                      label={candidate.position}
                      color="primary"
                      sx={{ borderRadius: 1 }}
                    />
                    <Chip
                      label={candidate.department}
                      color="secondary"
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                    <Chip
                      label={candidate.status}
                      sx={{
                        textTransform: 'capitalize',
                        bgcolor: alpha(statusColors[candidate.status], 0.1),
                        color: statusColors[candidate.status],
                        fontWeight: 500,
                        borderRadius: 1,
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Rating:</Typography>
                    <Rating value={candidate.rating} readOnly size="small" />
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CalendarToday fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Applied: {new Date(candidate.appliedDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessCenter fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Experience: {experienceLabels[candidate.experience]}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Source fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    Source: {candidate.source}
                  </Typography>
                </Box>
                
                {candidate.salary && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoney fontSize="small" color="action" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      Expected Salary: ${candidate.salary.toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>
      
      {/* Tabs section */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="candidate details tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Tab 
            label="Details" 
            icon={<Person />} 
            iconPosition="start" 
            sx={{ textTransform: 'none', minHeight: 48 }}
          />
          <Tab 
            label="Resume" 
            icon={<Description />} 
            iconPosition="start" 
            sx={{ textTransform: 'none', minHeight: 48 }}
          />
          <Tab 
            label="Notes" 
            icon={<Notes />} 
            iconPosition="start" 
            sx={{ textTransform: 'none', minHeight: 48 }}
          />
        </Tabs>
        
        {/* Details tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Contact Information</Typography>
                </Box>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Email color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Address"
                      secondary={candidate.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Phone color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Phone"
                      secondary={candidate.phone}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LocationOn color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Location"
                      secondary={candidate.location}
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Skills</Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {candidate.skills.map((skill) => (
                      <Chip
                        key={skill}
                        label={skill}
                        sx={{
                          bgcolor: isDark 
                            ? alpha(theme.palette.primary.main, 0.1) 
                            : alpha(theme.palette.primary.main, 0.05),
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Card>
            </Grid>
            
            {candidate.salary && (
              <Grid item xs={12}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Salary Information</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachMoney fontSize="medium" color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      Expected Salary: <strong>${candidate.salary.toLocaleString()}</strong> per year
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>
        
        {/* Resume tab */}
        <TabPanel value={tabValue} index={1}>
          <Card 
            variant="outlined" 
            sx={{ 
              borderRadius: 2,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Description fontSize="large" color="primary" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Resume Preview</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              The resume file ({candidate.resume}) is available for download or preview.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Download Resume
              </Button>
              <Button 
                variant="outlined" 
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Preview
              </Button>
            </Box>
          </Card>
        </TabPanel>
        
        {/* Notes tab */}
        <TabPanel value={tabValue} index={2}>
          <Card 
            variant="outlined" 
            sx={{ 
              borderRadius: 2,
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Notes</Typography>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<Edit />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Edit Notes
              </Button>
            </Box>
            
            {candidate.notes ? (
              <Typography variant="body1">
                {candidate.notes}
              </Typography>
            ) : (
              <Box 
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: isDark 
                    ? alpha(theme.palette.primary.main, 0.05)
                    : alpha(theme.palette.primary.main, 0.02),
                  borderRadius: 2,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Notes sx={{ fontSize: 48, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  No notes available for this candidate
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Add notes about interviews, impressions, or next steps
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<Edit />}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Add Note
                </Button>
              </Box>
            )}
          </Card>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default CandidateDetail;